import { useEffect, useState } from 'react';
import { apiClient, ApiError } from '@/lib/api/client';
import { cn } from '@/lib/utils';
import { useDialogFocus } from '@/lib/useDialogFocus';
import { QuietHoursForm } from '@/components/QuietHoursForm';

interface Props {
  onClose: () => void;
}

type Status =
  | { kind: 'loading' }
  | { kind: 'unlinked' }
  | {
      kind: 'linked';
      telegram_username: string | null;
      linked_at: string;
      paused_until: string | null;
      timezone: string | null;
      quiet_start_local: string | null;
      quiet_end_local: string | null;
      tg_chat_batch_threshold: number;
      tg_chat_batch_minutes: number;
    }
  | { kind: 'error'; message: string };

type LinkOffer = {
  deep_link: string;
  bot_username: string;
  expires_at: string;
};

/**
 * Telegram connection modal. Shows current link state; offers a deep-link to
 * `t.me/<bot>?start=<token>` for linking, plus an unlink button.
 *
 * The link flow:
 *  1. User clicks "Connect Telegram" → POST /v1/me/telegram/link-token
 *  2. Modal shows a "Open Telegram" button + the deep link as a copyable URL
 *  3. User opens TG → taps Start → bot exchanges the token for chat_id
 *  4. We poll /status every 2s for up to 60s after the deep-link click; on
 *     `linked:true` we transition the UI without requiring a refresh.
 */
export function TelegramModal({ onClose }: Props) {
  const [status, setStatus] = useState<Status>({ kind: 'loading' });
  const [offer, setOffer] = useState<LinkOffer | null>(null);
  const [requesting, setRequesting] = useState(false);
  const [polling, setPolling] = useState(false);
  const dialogRef = useDialogFocus<HTMLDivElement>();

  async function refresh() {
    try {
      const r = await apiClient.telegramStatus();
      if (r.linked) {
        setStatus({
          kind: 'linked',
          telegram_username: r.telegram_username,
          linked_at: r.linked_at!,
          paused_until: r.paused_until,
          timezone: r.timezone,
          quiet_start_local: r.quiet_start_local,
          quiet_end_local: r.quiet_end_local,
          tg_chat_batch_threshold: r.tg_chat_batch_threshold,
          tg_chat_batch_minutes: r.tg_chat_batch_minutes,
        });
        setOffer(null);
        setPolling(false);
      } else {
        setStatus({ kind: 'unlinked' });
      }
    } catch (e) {
      setStatus({
        kind: 'error',
        message: e instanceof ApiError ? e.message : 'Failed to load',
      });
    }
  }

  useEffect(() => {
    refresh();
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll for link completion after deep-link is opened
  useEffect(() => {
    if (!polling) return;
    const interval = setInterval(() => {
      refresh();
    }, 2000);
    const timeout = setTimeout(() => {
      setPolling(false);
    }, 60_000);
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polling]);

  async function startLink() {
    setRequesting(true);
    try {
      const o = await apiClient.telegramLinkToken();
      setOffer(o);
    } catch (e) {
      setStatus({
        kind: 'error',
        message: e instanceof ApiError ? e.message : 'Failed to mint link token',
      });
    } finally {
      setRequesting(false);
    }
  }

  const [pendingUnlink, setPendingUnlink] = useState(false);
  async function unlink() {
    if (!pendingUnlink) {
      setPendingUnlink(true);
      setTimeout(() => setPendingUnlink(false), 3000);
      return;
    }
    setPendingUnlink(false);
    try {
      await apiClient.telegramUnlink();
      refresh();
    } catch (e) {
      setStatus({
        kind: 'error',
        message: e instanceof ApiError ? e.message : 'Failed to unlink',
      });
    }
  }

  // Guard backdrop-close during the active link-flow — clicking off-modal
  // mid-process (e.g. tabbing to Telegram, clicking back into the browser
  // slightly off-modal) voids the freshly-minted token. Explicit Close
  // button still works.
  const linkFlowActive = status.kind === 'unlinked' && offer !== null;
  function backdropClose() {
    if (linkFlowActive) return;
    onClose();
  }
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="tg-modal-title"
      aria-describedby="tg-modal-desc"
      className="fixed inset-0 z-50 grid place-items-center bg-black/70 px-4 animate-in fade-in-0 duration-150"
      onClick={backdropClose}
    >
      <div
        ref={dialogRef}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg space-y-5 rounded-2xl border border-border bg-popover p-7 shadow-2xl animate-in zoom-in-95 fade-in-0 duration-150"
      >
        <div className="space-y-2">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Telegram delivery
          </div>
          <h2 id="tg-modal-title" className="text-2xl font-semibold tracking-tight">Triage from your phone</h2>
          <p id="tg-modal-desc" className="text-sm text-muted-foreground">
            Get DMs from <span className="font-mono text-foreground">@apetech_bot</span> for new
            urgent events + your assigned items. Tap inline buttons to mark Done, snooze 1h /
            tomorrow / Monday, or convert to a task — without leaving the chat.
          </p>
        </div>

        {status.kind === 'loading' && (
          <div className="h-12 animate-pulse rounded-md bg-background/40" aria-busy="true" aria-label="Loading Telegram status" />
        )}

        {status.kind === 'error' && (
          <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm">
            {status.message}
          </div>
        )}

        {status.kind === 'unlinked' && !offer && (
          <div className="space-y-3">
            <button
              type="button"
              onClick={startLink}
              disabled={requesting}
              className="w-full rounded-md bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
            >
              {requesting ? 'Generating link…' : 'Connect Telegram'}
            </button>
            <ul className="space-y-1.5 text-xs text-muted-foreground">
              <li>1. Click the button above — we'll mint a one-time link token (valid 15 min).</li>
              <li>2. Open Telegram via the deep link.</li>
              <li>3. Tap Start in the chat with @apetech_bot.</li>
              <li>4. This panel updates automatically when linked.</li>
            </ul>
            <details className="text-xs text-muted-foreground">
              <summary className="cursor-pointer hover:text-foreground">
                Don't have Telegram?
              </summary>
              <div className="mt-2 space-y-2 rounded-md border border-border bg-background/40 p-3">
                <p>
                  Install it (free, takes ~30 seconds):{' '}
                  <a
                    href="https://apps.apple.com/app/telegram-messenger/id686449807"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline-offset-2 hover:underline"
                  >
                    iOS
                  </a>
                  {' · '}
                  <a
                    href="https://play.google.com/store/apps/details?id=org.telegram.messenger"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline-offset-2 hover:underline"
                  >
                    Android
                  </a>
                  {' · '}
                  <a
                    href="https://desktop.telegram.org/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline-offset-2 hover:underline"
                  >
                    Desktop
                  </a>
                  .
                </p>
                <p>
                  Or skip — email digest delivery is enabled by default for all events. You won't
                  miss anything.
                </p>
              </div>
            </details>
          </div>
        )}

        {status.kind === 'unlinked' && offer && (
          <div className="space-y-4">
            <a
              href={offer.deep_link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setPolling(true)}
              className="block w-full rounded-md bg-primary px-4 py-3 text-center text-sm font-semibold text-primary-foreground hover:bg-primary/90 active:scale-[0.98]"
            >
              Open Telegram & tap Start
            </a>
            <div className="space-y-1">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Or copy the link
              </div>
              <div className="flex gap-2">
                <code className="block min-w-0 flex-1 overflow-x-auto rounded-md border border-border bg-background px-3 py-2 font-mono text-[11px] text-foreground">
                  {offer.deep_link}
                </code>
                <button
                  onClick={() => navigator.clipboard?.writeText(offer.deep_link)}
                  className="rounded-md border border-border px-3 py-2 text-xs text-muted-foreground hover:border-ring/60 hover:text-foreground"
                >
                  Copy
                </button>
              </div>
            </div>
            <div className="rounded-md border border-border bg-background p-3 text-xs text-muted-foreground">
              {polling ? (
                <span>
                  <span className="mr-2 inline-block size-2 animate-pulse rounded-full bg-primary" />
                  Watching for link…
                </span>
              ) : (
                <span>
                  Token expires {new Date(offer.expires_at).toLocaleString()}. Re-open the deep link
                  in Telegram if you didn't tap Start yet.
                </span>
              )}
            </div>
          </div>
        )}

        {status.kind === 'linked' && (
          <div className="space-y-4">
            <div className="rounded-md border border-primary/40 bg-primary/10 px-4 py-3">
              <div className="text-sm font-semibold text-foreground">
                Linked
                {status.telegram_username && (
                  <span className="ml-2 font-mono text-xs text-muted-foreground">
                    @{status.telegram_username}
                  </span>
                )}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                Connected {new Date(status.linked_at).toLocaleString()}.
                {status.paused_until && (
                  <div className="mt-1 text-amber-400">
                    🔕 Paused until {new Date(status.paused_until).toLocaleString()}.
                    Send <code className="font-mono">/resume</code> to the bot to un-pause.
                  </div>
                )}
              </div>
            </div>
            <ul className="space-y-1.5 text-xs text-muted-foreground">
              <li>Bot commands: <code>/pause 4</code> · <code>/resume</code> · <code>/status</code> · <code>/help</code></li>
              <li>You'll get DMs for: urgent events, events assigned to you, events routed into projects you own.</li>
            </ul>
            <QuietHoursForm
              timezone={status.timezone}
              quietStart={status.quiet_start_local}
              quietEnd={status.quiet_end_local}
              onSaved={refresh}
            />
            <ChatBatchForm
              threshold={status.tg_chat_batch_threshold}
              minutes={status.tg_chat_batch_minutes}
              onSaved={refresh}
            />
            <button
              type="button"
              onClick={unlink}
              aria-pressed={pendingUnlink}
              aria-describedby={pendingUnlink ? 'unlink-confirm-hint' : undefined}
              className={
                pendingUnlink
                  ? 'w-full rounded-md border border-destructive bg-destructive/15 px-4 py-2 text-sm font-semibold text-destructive transition-colors'
                  : 'w-full rounded-md border border-destructive/50 px-4 py-2 text-sm font-semibold text-destructive transition-colors hover:bg-destructive/10'
              }
            >
              {pendingUnlink ? 'Confirm unlink — you\'ll stop receiving DMs' : 'Unlink Telegram'}
            </button>
            {pendingUnlink && (
              <span id="unlink-confirm-hint" role="status" className="sr-only">
                Click again within 3 seconds to confirm unlinking Telegram.
              </span>
            )}
          </div>
        )}

        <button
          onClick={onClose}
          className="w-full rounded-md border border-border bg-background px-4 py-2 text-xs text-muted-foreground hover:border-ring/60 hover:text-foreground"
        >
          Close
        </button>
      </div>
    </div>
  );
}

/**
 * Per-user chat-batching settings. Two knobs:
 *   - threshold (1-100): N chat messages to buffer before flushing
 *   - minutes  (1-1440): max wait since first buffered before flushing
 * Whichever trips first wins. threshold=1 effectively disables batching.
 */
function ChatBatchForm({
  threshold,
  minutes,
  onSaved,
}: {
  threshold: number;
  minutes: number;
  onSaved: () => void;
}) {
  const [t, setT] = useState(threshold);
  const [m, setM] = useState(minutes);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const dirty = t !== threshold || m !== minutes;
  async function save() {
    setSaving(true);
    setErr(null);
    try {
      await apiClient.telegramUpdate({
        tg_chat_batch_threshold: t,
        tg_chat_batch_minutes: m,
      });
      onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }
  return (
    <div className="space-y-2 rounded-lg border border-border bg-card/40 p-3">
      <div className="space-y-1">
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Chat digest
        </div>
        <p className="text-xs text-muted-foreground">
          Bundle chat messages into one digest DM. Sends as soon as either
          limit is hit. Set count to 1 to get every message instantly.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <label className="space-y-1 text-xs text-muted-foreground">
          <span className="block font-mono text-[10px] uppercase tracking-widest">After N msgs</span>
          <input
            type="number"
            inputMode="numeric"
            min={1}
            max={100}
            value={t}
            onChange={(e) => setT(Math.max(1, Math.min(100, Number(e.target.value) || 1)))}
            className="block w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
          />
        </label>
        <label className="space-y-1 text-xs text-muted-foreground">
          <span className="block font-mono text-[10px] uppercase tracking-widest">Or N minutes</span>
          <input
            type="number"
            inputMode="numeric"
            min={1}
            max={1440}
            value={m}
            onChange={(e) => setM(Math.max(1, Math.min(1440, Number(e.target.value) || 1)))}
            className="block w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
          />
        </label>
      </div>
      {err && (
        <div role="alert" className="rounded border border-destructive/40 bg-destructive/10 px-2 py-1 text-xs">
          {err}
        </div>
      )}
      <div className="flex items-center justify-end">
        <button
          type="button"
          onClick={save}
          disabled={!dirty || saving}
          className="rounded-md bg-primary px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-primary-foreground disabled:opacity-50"
        >
          {saving ? '…' : 'Save'}
        </button>
      </div>
    </div>
  );
}
